import React, { useState, useEffect } from 'react';

import {
  Paper,
  Typography,
  Dialog,
  TextField,
  CircularProgress,
} from '@material-ui/core';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { observer } from 'mobx-react-lite';

import {
  GoogleLogin,
  GoogleLoginResponse,
  GoogleLoginResponseOffline,
} from 'react-google-login';
import { toast } from 'react-toastify';
import { InputContainer } from './styles';
import { Button } from '../../generators/FormGenerator/styles';
import { OAuthCredentialInput } from '../../store/inputs/OAuthCredentialInput';
import { instance } from '../../services/api';
import CAssetInput from './CAssetInput';
import { AssetInput } from '../../store/inputs/AssetInput';
import { InputType } from '../../store/inputs/types';

const COAuthCredentialInput = observer(
  ({ item }: { item: OAuthCredentialInput }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [oauthValue, setOauthValue] = useState<Record<string, any> | null>(
      null,
    );
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [authServerClient] = useState<AssetInput>(
      new AssetInput({
        tag: 'authServerClient',
        label: 'Authentication Server Client',
        required: true,
        dataType: 'openidAuthServerClient',
        placeholder: '',
        disabled: false,
        error: '',
        route: '',
      } as InputType),
    );

    const { value, onChange, tag } = item;

    useEffect(() => {
      if (!value) setOauthValue(null);
    }, [value]);

    const loginAuth = () => {
      setLoading(true);
      instance
        .post('/api/oauth/login', {
          username,
          password,
          route: authServerClient.selectorValue
            ? (authServerClient.selectorValue as any).value?.url
            : '',
          clientId: authServerClient.selectorValue
            ? (authServerClient.selectorValue as any).value?.clientId
            : '',
          clientSecret: authServerClient.selectorValue
            ? (authServerClient.selectorValue as any).value?.clientSecret
            : '',
          getSub: true,
        })
        .then((res) => {
          const resData = res.data;

          setOauthValue(resData && resData.data ? resData.data : null);
          onChange(resData && resData.data ? resData.data : null);
          setOpenDialog(false);
          setErrorMsg('');
        })
        .catch((err) => {
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error_description
          )
            setErrorMsg(err.response.data.error_description);
          else if (
            err &&
            err.response &&
            err.response.data &&
            typeof err.response.data === 'string'
          )
            setErrorMsg(err.response.data);
          else
            setErrorMsg(
              err && err.message ? err.message : 'Authentication failed',
            );
        })
        .finally(() => setLoading(false));
    };

    return (
      <div>
        <Dialog open={openDialog}>
          <DialogTitle id="form-dialog-title">Sign in</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              {authServerClient.value
                ? authServerClient.value.url
                : 'OpenId token URL'}
            </Typography>
          </DialogContent>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              id="username"
              label="Username"
              value={username}
              disabled={loading}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              id="password"
              label="Password"
              type="password"
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
            />
            {loading ? (
              <CircularProgress style={{ marginTop: '2%' }} />
            ) : (
              <Typography gutterBottom>{errorMsg}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => loginAuth()} color="primary">
              Sign in
            </Button>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Paper elevation={3}>
          <InputContainer>
            <div>
              <CAssetInput key={authServerClient.tag} item={authServerClient} />
            </div>

            {authServerClient.selectorValue &&
            // eslint-disable-next-line
            (authServerClient.selectorValue as any).value?.url === 'google' ? (
              <GoogleLogin
                clientId={
                  (authServerClient.selectorValue as any)
                    ? (authServerClient.selectorValue as any).value
                        ?.clientId
                    : ''
                }
                buttonText="Login with Google Account"
                onSuccess={(
                  response: GoogleLoginResponse | GoogleLoginResponseOffline,
                ) => {
                  setOauthValue({
                    sub: (response as GoogleLoginResponse).googleId,
                    token: (response as GoogleLoginResponse).tokenObj.id_token,
                  });
                  onChange({
                    sub: (response as GoogleLoginResponse).googleId,
                    token: (response as GoogleLoginResponse).tokenObj.id_token,
                  });
                }}
                onFailure={() => {
                  toast.error('Google login fail');
                }}
                cookiePolicy="single_host_origin"
              />
            ) : (
              <div>
                <Button
                  disabled={
                    !(
                      authServerClient.selectorValue &&
                      (authServerClient.selectorValue as any).value
                        ?.url !== 'google'
                    )
                  }
                  onClick={() => setOpenDialog(true)}
                >
                  Login
                </Button>
              </div>
            )}
            {tag === 'openIDSub' ? (
              <div style={{ marginTop: '2%' }}>
                <TextField
                  disabled
                  fullWidth
                  id={tag}
                  label=""
                  style={{ marginTop: '2%' }}
                  placeholder="OpenID Subject"
                  value={oauthValue ? oauthValue.sub : ''}
                />
              </div>
            ) : tag === 'openIDToken' ? (
              <div style={{ marginTop: '2%' }}>
                <TextField
                  disabled
                  fullWidth
                  multiline
                  id={tag}
                  label=""
                  rowsMax={10}
                  placeholder="JWT Token"
                  value={oauthValue ? oauthValue.token : ''}
                />
              </div>
            ) : null}
          </InputContainer>
        </Paper>
      </div>
    );
  },
);

export default COAuthCredentialInput;
